import { useState, useRef, useEffect, useMemo } from "react";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";
import {
  selectSubscription,
  selectSubscriptionLoaded,
} from "store/SubscriptionSlice";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  IconButton,
} from "@mui/material";
import WebStoriesIcon from "@mui/icons-material/WebStories";
// import SoupKitchenIcon from "@mui/icons-material/SoupKitchen"; // used by Get Help - hidden
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
// project imports
import MainCard from "uicomponent/cards/MainCard";
import Transitions from "uicomponent/extended/Transitions";
import UpgradePlanCard from "./UpgradePlanCard";
import User1 from "assets/images/users/user-round.svg";
import { useThemeContext } from "themes/CustomThemeProvider";
import BackgroundSelectionModal from "components/BackgroundSelectionModal";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SettingsIcon from "@mui/icons-material/Settings";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import ModeNightRoundedIcon from "@mui/icons-material/ModeNightRounded";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
// assets
import {
  IconLogout,
  IconSearch,
  IconSettings,
  IconUser,
} from "@tabler/icons-react";

// ==============================|| PROFILE MENU ||============================== //

const ProfileMenu = () => {
  const { logout, currentUser } = useSupabaseAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const colorMode = useThemeContext();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false);
  const preferences = useSelector(
    (state) => state.preferenceReducer.preferences
  );
  const subscription = useSelector(selectSubscription);
  const subscriptionLoaded = useSelector(selectSubscriptionLoaded);

  // Determine if user is free tier based on SubscriptionSlice
  // Free tier is when tier === "free" or tier is null/undefined
  const isFreeUser = !subscription || subscription?.tier === "free";

  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);
  const handleLogout = async () => {
    try {
      setOpen(false);
      const { error } = await logout();
      if (error) {
        console.error("Logout error:", error);
      } else {
        navigate("/sign-in");
      }
    } catch (error) {
      console.error("Unexpected error during logout:", error);
      // Still navigate even if there's an error
      navigate("/sign-in");
    }
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    await handleLogout();
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListItemClick = (event, index, route = "") => {
    setSelectedIndex(index);
    handleClose(event);

    if (route && route !== "") {
      navigate(route);
    }
  };
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const customBorderRadius = "10";

  // Determine avatar URL - prioritize preferences.avatar_url over user_metadata
  const avatarUrl = useMemo(() => {
    return (
      preferences?.avatar_url ||
      currentUser?.user_metadata?.avatar_url ||
      currentUser?.user_metadata?.picture ||
      ""
    );
  }, [
    preferences?.avatar_url,
    currentUser?.user_metadata?.avatar_url,
    currentUser?.user_metadata?.picture,
  ]);

  return (
    <>
      <IconButton
        sx={{ mr: 1, width: "48px", height: "48px" }}
        onClick={colorMode.toggleColorMode}
        aria-label="toggle dark mode"
      >
        {theme.palette.mode === "dark" ? (
          <WbSunnyRoundedIcon />
        ) : (
          <ModeNightRoundedIcon />
        )}
      </IconButton>
      <Chip
        sx={{
          height: "48px",
          alignItems: "center",
          borderRadius: "27px",
          transition: "all .2s ease-in-out",
          borderColor: theme.palette.divider,
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background:
              theme.palette.mode === "dark"
                ? `rgba(33, 150, 243, 0.15)!important`
                : `${theme.palette.primary.main}!important`,
            color: theme.palette.common.white,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 2px 12px rgba(33, 150, 243, 0.25)"
                : "none",
            "& svg": {
              stroke: theme.palette.common.white,
            },
          },
          "& .MuiChip-label": {
            lineHeight: 0,
          },
        }}
        icon={
          <Avatar
            src={avatarUrl}
            sx={{
              ...theme.custom.componentStyles.mediumAvatar,
              margin: "8px 0 8px 8px !important",
              cursor: "pointer",
            }}
            ref={anchorRef}
            aria-controls={open ? "menu-list-grow" : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={
          <IconSettings
            stroke={1.5}
            size="1.5rem"
            color={theme.palette.primary.main}
          />
        }
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  elevation={16}
                  content={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                >
                  {/* <PerfectScrollbar
                    style={
                      {
                        height: "100%",
                        maxHeight: "calc(100vh - 250px)",
                        overflowX: "hidden",
                      }
                    }
                  > */}
                  <Box sx={{ px: 2, py: 0 }}>
                    <List
                      component="nav"
                      sx={{
                        width: "100%",
                        maxWidth: 350,
                        minWidth: 300,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "10px",
                        [theme.breakpoints.down("md")]: {
                          minWidth: "100%",
                        },
                        "& .MuiListItemButton-root": {
                          mt: 0.5,
                        },
                      }}
                    >
                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(
                            event,
                            "app-settings",
                            "/settings"
                          )
                        }
                      >
                        <ListItemIcon>
                          <ManageAccountsIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>Account Settings</>
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(
                            event,
                            "account",
                            "/financial-settings"
                          )
                        }
                      >
                        <ListItemIcon>
                          <CurrencyExchangeIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>Financial Settings & Goals</>
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(event, "account", "/calendars")
                        }
                      >
                        <ListItemIcon>
                          <CalendarMonthIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>Calendars</>
                      </ListItemButton>

                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(event, "account", "/history")
                        }
                      >
                        <ListItemIcon>
                          <WebStoriesIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>History & Activities</>
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(
                            event,
                            "invoice-settings",
                            "/invoice-settings"
                          )
                        }
                      >
                        <ListItemIcon>
                          <SettingsIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>

                        <>Invoice Settings</>
                      </ListItemButton>
                      {/* Get Help! - hidden for now
                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(event, "account", "/help")
                        }
                      >
                        <ListItemIcon>
                          <SoupKitchenIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>Get Help!</>
                      </ListItemButton>
                      */}

                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) =>
                          handleListItemClick(event, "account", "/subscription")
                        }
                      >
                        <ListItemIcon>
                          <EventRepeatIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>My Subscription</>
                      </ListItemButton>

                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 0}
                        onClick={(event) => {
                          setOpen(false);
                          setBackgroundModalOpen(true);
                        }}
                      >
                        <ListItemIcon>
                          <WallpaperIcon stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>Customize Background</>
                      </ListItemButton>

                      {/* Only show UpgradePlanCard for free tier users */}
                      {subscriptionLoaded && isFreeUser && (
                        <UpgradePlanCard
                          onNavigate={() => {
                            setOpen(false);
                            navigate("/subscription");
                          }}
                        />
                      )}

                      <ListItemButton
                        sx={{
                          borderRadius: `${customBorderRadius}px`,
                        }}
                        selected={selectedIndex === 4}
                        onClick={handleLogoutClick}
                      >
                        <ListItemIcon>
                          <IconLogout stroke={1.5} size="1.3rem" />
                        </ListItemIcon>
                        <>Logout</>
                      </ListItemButton>
                    </List>
                  </Box>
                  {/* </PerfectScrollbar> */}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="logout-dialog-title">Confirm logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sign out of Fractional? Any unsaved changes
            will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="primary"
            autoFocus
          >
            Log out
          </Button>
        </DialogActions>
      </Dialog>
      <BackgroundSelectionModal
        open={backgroundModalOpen}
        onClose={() => setBackgroundModalOpen(false)}
      />
    </>
  );
};

export default ProfileMenu;
